import React, { useEffect } from 'react';
import styles from './Pages.module.scss';
import globalStyles from 'common/global.module.scss';
import {
  Layout,
  Row,
  Col,
  Typography,
  Menu,
  Icon,
  Divider,
  Button,
  Avatar,
  Alert,
  Breadcrumb,
  Spin,
  Dropdown,
} from 'antd';
import logo from 'assets/img/logo.png';
import {
  Switch,
  Route,
  Redirect,
  Link,
  useRouteMatch,
  useLocation,
  useHistory,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'store';
import { Sizes, LOADING, TableSizeNames } from 'common';
import { loginPagesLoading } from './Login';
import { searchHistoryClear } from './Search';
import { MenuItem, routes } from 'pages';
import { disclaimer1, disclaimer2 } from './constants';
import {
  getUserUsageReportAccessFlag,
  userUsageReportAccessFlagSelector,
  userUsageReportClear,
} from './UserUsageReport';
import { InteractionStatus } from '@azure/msal-browser';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { getAccessToken, logoutAdUser } from 'common/helpers';
import { AzureAdClaims } from 'common/models';
import {
  editAuthenticationPhonePolicy,
  editAuthenticationPhoneRequest,
  editEmailPolicy,
  editEmailRequest,
  profileEditPolicy,
  profileEditRequest,
  profileViewPolicy,
  profileViewRequest,
  resetPasswordPolicy,
  resetPasswordRequest,
} from 'authConfig';
import ButtonGroup from 'antd/lib/button/button-group';
import MaintenanceMessage from 'common/components/MaintenanceMessage/MaintenanceMessage';

const { Header, Footer, Sider, Content } = Layout;
const { Text, Title, Paragraph } = Typography;

const Pages: React.FC = () => {
  const { path, url } = useRouteMatch();
  const { pathname } = useLocation();
  const login = useSelector((state: AppState) => state.login);
  const pagesLoading = login.pagesLoading;
  const isAuthenticated = useIsAuthenticated();
  const userUsageReportAccessFlag = useSelector((state: AppState) =>
    userUsageReportAccessFlagSelector(state),
  );
  const dispatch = useDispatch();
  const uriArray = pathname.split('/');
  const menuItems: MenuItem[] = Object.values(routes).filter(
    ({ visible }) => visible,
  );

  const current = menuItems
    .filter((item) => item.link === uriArray[uriArray.length - 1])
    .map((item) => item.text)[0];

  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});
  const refreshAdUserSession: any = localStorage.getItem(
    'refreshadusersession',
  );
  const isPublicUser: any = localStorage.getItem('ispublicuser');
  const isWellsFargoUser: any = localStorage.getItem('iswellsfargouser');
  const history = useHistory();

  const triggerAdPolicy = (policy: string) => {
    let request;
    switch (policy) {
      case profileViewPolicy:
        request = profileViewRequest;
        break;
      case profileEditPolicy:
        request = profileEditRequest;
        break;
      case resetPasswordPolicy:
        request = resetPasswordRequest;
        break;
      case editAuthenticationPhonePolicy:
        request = editAuthenticationPhoneRequest;
        break;
      case editEmailPolicy:
        request = editEmailRequest;
        break;
    }

    if (request) instance.loginRedirect(request);
  };

  const getAvatarCharacter = (azureAdClaims: AzureAdClaims | undefined) => {
    let avatarCharacterToDisplay = '';
    if (azureAdClaims?.onpremADusername) {
      const indexOfLastBackslash =
        azureAdClaims.onpremADusername.lastIndexOf('\\');
      avatarCharacterToDisplay = azureAdClaims.onpremADusername
        .substring(indexOfLastBackslash + 1, indexOfLastBackslash + 2)
        ?.toUpperCase();
    } else if (azureAdClaims?.UserName) {
      avatarCharacterToDisplay = azureAdClaims.UserName.substring(
        0,
        1,
      )?.toUpperCase();
    }
    return avatarCharacterToDisplay;
  };

  const getUserNameDisplay = (azureAdClaims: AzureAdClaims | undefined) => {
    let userNameToDisplay = '';
    if (azureAdClaims?.onpremADusername) {
      userNameToDisplay = azureAdClaims?.onpremADemailaddress
        ? `${azureAdClaims.onpremADusername} (${azureAdClaims.onpremADemailaddress})`
        : azureAdClaims.onpremADusername;
    } else if (azureAdClaims?.UserName) {
      userNameToDisplay = azureAdClaims?.Email
        ? `${azureAdClaims.UserName} (${azureAdClaims.Email})`
        : azureAdClaims.UserName;
    }
    return userNameToDisplay;
  };

  // clear user usage report state whenever user is no longer logged in
  useEffect(() => {
    if (!isAuthenticated) dispatch(userUsageReportClear());
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    // log the user back in if they pressed the refresh button and still have a session that is not expired
    if (localStorage.getItem('refreshadusersession') === 'true') {
      if (isPublicUser === 'true') {
        if (isWellsFargoUser === 'true') {
          history.push(
            `/public/wells-fargo-signin?return_to=${encodeURIComponent(
              pathname,
            )}`,
          );
        } else {
          history.push(
            `/public/signin?return_to=${encodeURIComponent(pathname)}`,
          );
        }
      } else if (isPublicUser === 'false') {
        history.push(
          `/public/employee-signin?return_to=${encodeURIComponent(pathname)}`,
        );
      }
    }
  }, [history, isPublicUser, isWellsFargoUser, pathname, refreshAdUserSession]);

  // user authorization and user usage report flag retrieval for azure ad login functionality
  useEffect(() => {
    if (account && inProgress === InteractionStatus.None) {
      // set pages to loading while we confirm the user's access after logging in
      dispatch(loginPagesLoading(true));
      getAccessToken(instance, account, inProgress)
        .then((response) => {
          if (response?.accessToken) {
            dispatch(
              getUserUsageReportAccessFlag(response.accessToken, instance),
            );
          }
        })
        .catch(() => {
          // failed to retrieve access token
          logoutAdUser(instance);
        });
    }
  }, [dispatch, instance, account, inProgress]);

  const azureAdClaims: AzureAdClaims | undefined = account?.idTokenClaims as
    | AzureAdClaims
    | undefined;

  if (!isAuthenticated) {
    const isPublicUser = localStorage.getItem('ispublicuser');
    if (isPublicUser !== 'true' && isPublicUser !== 'false') {
      localStorage.removeItem('refreshadusersession');
    } else {
      localStorage.setItem('refreshadusersession', 'true');
    }
  }

  return (
    // If you are authenticated - go to the application else - go to login page (it is done for manual URI changes)
    isAuthenticated ? (
      <>
        <Spin spinning={pagesLoading} size={Sizes.LARGE} tip={LOADING}>
          <Layout className={'min-vh-100'}>
            <Sider
              breakpoint="lg"
              collapsible
              data-cy="nav-menu"
              style={{ minHeight: '100vh' }}
            >
              <Row
                type="flex"
                justify="center"
                align="middle"
                className={styles.logo}
              >
                <img src={logo} alt="" />
              </Row>
              <Menu
                selectedKeys={[pathname]}
                theme="dark"
                defaultSelectedKeys={[routes.dashboard.link]}
              >
                {menuItems
                  .filter(
                    (item) =>
                      item.version.indexOf(
                        process.env.REACT_APP_VERSION as string,
                      ) !== -1,
                  )
                  .sort((a, b) => a.index - b.index)
                  .map((item) => {
                    return (
                      (!item.requiresBillingInfoAccess ||
                        userUsageReportAccessFlag) && (
                        <Menu.Item
                          key={`${url}/${item.link}`}
                          title={item.header}
                          // id={item.link}
                          data-cy={item.link}
                        >
                          <Link to={`${url}/${item.link}`}>
                            <Icon type={item.icon} />
                            <Text className={styles.text}>{item.text}</Text>
                          </Link>
                        </Menu.Item>
                      )
                    );
                  })}
              </Menu>
            </Sider>
            <Layout>
              <Header className={styles.header}>
                <Row
                  type="flex"
                  justify="space-between"
                  align="middle"
                  className="h-100"
                >
                  <Col xs={0} sm={0} md={12}>
                    <Title level={4} className={styles.text}>
                      Title and Property Report Services
                    </Title>
                  </Col>
                  {isAuthenticated && account && (
                    <Col xs={24} md={12}>
                      <Row type="flex" justify="end" align="middle">
                        <Avatar>{getAvatarCharacter(azureAdClaims)}</Avatar>
                        &nbsp;
                        <Text className={`${styles.text}`} data-cy="user-name">
                          {getUserNameDisplay(azureAdClaims)}
                        </Text>
                        {isAuthenticated && account && azureAdClaims?.UserName && (
                          <ButtonGroup size="small">
                            <Dropdown
                              overlay={
                                <Menu
                                  onClick={({ key }) => triggerAdPolicy(key)}
                                >
                                  <Menu.Item key={profileViewPolicy}>
                                    View Profile
                                  </Menu.Item>
                                </Menu>
                              }
                            >
                              <Button
                                type="link"
                                size={TableSizeNames.SMALL}
                                shape="round"
                              >
                                Manage Account&nbsp;
                                <Icon type="down" />
                              </Button>
                            </Dropdown>
                          </ButtonGroup>
                        )}
                        <Button
                          onClick={() => {
                            dispatch(searchHistoryClear());
                            if (isAuthenticated && account) {
                              logoutAdUser(instance);
                            }
                          }}
                          type="link"
                          size="small"
                          data-cy="singout-button"
                        >
                          Sign out
                        </Button>
                      </Row>
                    </Col>
                  )}
                </Row>
              </Header>
              <Content className={styles.content}>
                {current && (
                  <Row type="flex" justify="space-between">
                    <Breadcrumb>
                      <Breadcrumb.Item>
                        <Link to={`${url}/${routes.dashboard.link}`}>
                          <Icon type={`${routes.dashboard.icon}`} />
                          &nbsp;
                          <Text>Dashboard</Text>
                        </Link>
                      </Breadcrumb.Item>
                      <Breadcrumb.Item>{current}</Breadcrumb.Item>
                    </Breadcrumb>
                    <Col
                      className={globalStyles.textCenter}
                      xs={24}
                      lg={6}
                      xl={10}
                      xxl={14}
                    >
                      <MaintenanceMessage></MaintenanceMessage>
                    </Col>
                    <Text strong>
                      Here is a{' '}
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://www.datatracetitle.com/hubfs/NYC-ONLINE-ABSTRACT-GUIDE-03-2020.pdf"
                      >
                        user guide
                      </a>{' '}
                      and{' '}
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://www.datatracetitle.com/hubfs/NYC-Online-Abstract-Tutorial.mp4"
                      >
                        video tutorial
                      </a>{' '}
                      for this site.
                    </Text>
                  </Row>
                )}
                <Switch>
                  <Redirect
                    from={`${path}/`}
                    to={`${path}/${routes.dashboard.link}`}
                    exact={true}
                  />
                  {Object.values(routes).map(({ link, component }) => (
                    <Route
                      path={`${path}/${link}`}
                      component={component}
                      key={link}
                    />
                  ))}
                  <Redirect to={`${path}/${routes.dashboard.link}`} />
                </Switch>
                <Divider dashed />
                <Alert
                  closable
                  showIcon
                  type="info"
                  message="Important"
                  description={
                    <>
                      <Paragraph>{disclaimer1}</Paragraph>
                      <Paragraph>{disclaimer2}</Paragraph>
                    </>
                  }
                />
              </Content>
              <Footer style={{ textAlign: 'center' }}>
                Copyright © 2007-{new Date().getFullYear()} DataTrace
                Information Services, LLC. All rights reserved.&nbsp;
                <a
                  href="https://www.firstam.com/privacy-policy/#type-of-information"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
              </Footer>
            </Layout>
          </Layout>
        </Spin>
      </>
    ) : (
      <Redirect
        to={`/public/login?return_to=${encodeURIComponent(pathname)}`}
      />
    )
  );
};

export default Pages;
